import React, { useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

/**
 * Locked-out admin self-recovery modal — Feb 2026.
 *
 * Hits POST /api/auth/recover-admin with the platform's High-King
 * recovery secret. On success, the two seeded admin accounts are
 * force-resynced to their known bcrypt password hashes and the
 * `requires_password_change` flag is cleared. The user can then log
 * in immediately with the documented admin credentials.
 *
 * Intentionally UNAUTHENTICATED — this is the "I'm locked out" escape
 * hatch. The secret phrase is the only gate.
 */
const RecoverAdminModal = ({ open, onOpenChange }) => {
  const [secret, setSecret] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/recover-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: secret.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(data.detail || 'Recovery failed');
        return;
      }
      setMsg('Admin accounts restored. Close this dialog and log in with your known password.');
    } catch (_e) {
      setErr('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-red-900/60 text-white" data-testid="recover-admin-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-red-400" />
            Recover Admin Access
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Enter your High-King recovery secret to reinstall the seeded admin passwords.
            The two known admin accounts will be re-synced to their documented credentials.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <Input
            type="password"
            placeholder="Recovery secret"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            className="bg-slate-800 border-red-900/50 text-white"
            data-testid="recover-admin-secret-input"
            autoFocus
            required
          />

          {err && (
            <div className="text-sm text-red-400" data-testid="recover-admin-error">{err}</div>
          )}
          {msg && (
            <div className="text-sm text-green-400" data-testid="recover-admin-success">{msg}</div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-slate-700 text-white"
              data-testid="recover-admin-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !secret.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="recover-admin-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recovering…
                </>
              ) : (
                'Recover'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RecoverAdminModal;
